import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router';
import { appPath } from '../constants/appBasePath';
import {
  DocumentTextIcon,
  Squares2X2Icon,
  UsersIcon,
  CloudIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import type { FC, SVGProps } from 'react';

type HeroIcon = FC<SVGProps<SVGSVGElement>>;

const features: { Icon: HeroIcon; label: string }[] = [
  { Icon: Squares2X2Icon, label: 'Everything in one secure dashboard' },
  { Icon: DocumentTextIcon, label: 'View your documents and records' },
  { Icon: UsersIcon, label: 'Share access with an advisor' },
  {
    Icon: CloudIcon,
    label: 'Securely store and retrieve your documents anytime',
  },
  { Icon: BellIcon, label: 'Renewal reminders and product updates' },
];

const pills = [
  'Estate planning',
  'Tax vault',
  'Insurance tracker',
  'Family calendar',
  '+ more',
];

const dashboardPath = appPath('/');

export default function LandingPage() {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ?? dashboardPath;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo);
    }
  }, [isAuthenticated, navigate, returnTo]);

  const handleSignup = () => {
    loginWithRedirect({
      authorizationParams: { screen_hint: 'signup' },
      appState: { returnTo },
    });
  };

  const handleSignIn = () => {
    loginWithRedirect({
      authorizationParams: { screen_hint: 'login' },
      appState: { returnTo },
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-[560px] overflow-hidden rounded-[10px] border border-theme-border bg-surface">
        <div className="px-7 py-10 sm:px-[52px] sm:py-12">
          {/* Eyebrow */}
          <div className="mb-8 flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-primary-600">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-600" />
            Digital Ascension Group
          </div>

          {/* Heading */}
          <h1 className="mb-4 max-w-[380px] text-[26px] font-semibold leading-tight tracking-[-0.015em] text-on-surface sm:text-[30px]">
            One account for your entire financial life
          </h1>

          {/* Lede */}
          <p className="mb-8 max-w-[400px] text-[14.5px] leading-relaxed text-on-surface-tertiary">
            Sign in to your dashboard — one secure place for your family's
            finances, documents, and plans.
          </p>

          {/* Features */}
          <div className="mb-9 flex flex-col gap-3.5">
            {features.map(({ Icon, label }) => (
              <div
                key={label}
                className="grid gap-3.5 text-[14px] leading-relaxed text-on-surface"
                style={{ gridTemplateColumns: '22px 1fr' }}
              >
                <Icon
                  className="mt-0.5 h-[18px] w-[18px] shrink-0 stroke-primary-600"
                  strokeWidth={1.6}
                />
                <span>{label}</span>
              </div>
            ))}
          </div>

          {/* Pills */}
          <p className="mb-3.5 text-[13px] text-on-surface-quaternary">
            Also get access to:
          </p>
          <div className="mb-9 flex flex-wrap gap-2">
            {pills.map((p) => (
              <span
                key={p}
                className="rounded-full border border-primary-600/50 px-4 py-[7px] text-[12.5px] text-primary-600"
              >
                {p}
              </span>
            ))}
          </div>

          {/* CTA */}
          <button
            id="auth0-signup"
            onClick={handleSignup}
            className="w-full rounded-none bg-gold-500 py-3.5 text-[14px] font-semibold text-ink transition-colors hover:bg-gold-600"
          >
            Create your account
          </button>
          <p className="mt-3 text-center text-[12.5px] text-on-surface-quaternary">
            Already have an account?{' '}
            <button
              id="auth0-signin"
              onClick={handleSignIn}
              className="font-medium text-primary-600 hover:underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
